// Local keystore: the user's sealed private-key bundle lives in IndexedDB
// (or localStorage fallback). Two wrappings of the same key material exist:
//   bundle.byPassword  = AES-GCM( keys, KDF(password) )
//   bundle.byPhrase    = AES-GCM( keys, KDF(phrase) )
// so EITHER secret can unseal. Argon2id/PBKDF2 and AES-GCM via WebCrypto;
// PQC key material itself comes from crypto-service / liboqs-wasm.
//
// This module is the integration seam for Forgot.jsx and Settings. The
// `useLocalKeystore` hook returns the operations the UI needs; swap the body
// of each for the real WebCrypto implementation (TODO markers).
import { useMemo } from 'react';
import { normalizeWords } from './recovery.js';

const KEY = (email) => `km-keystore:${email.toLowerCase()}`;
const read = (email) => { try { return JSON.parse(localStorage.getItem(KEY(email))); } catch { return null; } };
const write = (email, b) => localStorage.setItem(KEY(email), JSON.stringify(b));

export function useLocalKeystore() {
  return useMemo(() => ({
    has(email) { return !!read(email); },
    /** Store a freshly generated bundle (registration). TODO: real AES-GCM wrapping. */
    async create(email, { password, phrase, keys }) {
      write(email, { createdAt: Date.now(), phraseWords: normalizeWords(phrase), keys: keys || null, pwHint: password.length });
    },
    /** Compare the entered phrase with the local bundle. Returns { ok, mismatch } - mismatch is the first wrong word index. */
    async checkPhrase(email, words) {
      const b = read(email); if (!b) return { ok: false, mismatch: 0, reason: 'no local key bundle on this device' };
      const e = normalizeWords(words);
      const mismatch = b.phraseWords.findIndex((w, i) => w !== e[i]);
      return { ok: mismatch === -1, mismatch }; // TODO: derive KDF(phrase) and AES-GCM decrypt b.byPhrase instead of comparing words
    },
    async resealWithPassword(email, newPassword) {
      const b = read(email); if (!b) throw new Error('no local key bundle on this device');
      write(email, { ...b, pwHint: newPassword.length, resealedAt: Date.now() }); // TODO: re-wrap keys with KDF(newPassword)
    },
    /** Proof for the server that the phrase holder controls the signing key (sign a nonce). */
    async proof(email) { return { signedNonce: 'TODO-ml-dsa-signature', email }; },
    async rotatePhrase(email, phrase) { const b = read(email) || { createdAt: Date.now() }; write(email, { ...b, phraseWords: normalizeWords(phrase), phraseCreatedAt: Date.now() }); },
    phraseWords(email) { return read(email)?.phraseWords || null; },
    /** Persist the account's private PQC keys locally (device-bound). @param {{kem,sig}} privateKeys */
    saveIdentity(email, privateKeys) { const b = read(email) || { createdAt: Date.now() }; write(email, { ...b, identity: privateKeys || null }); },
    /** The locally-stored private PQC keys for this account, or null. */
    identity(email) { return read(email)?.identity || null; },
    // Device-local cache of the unlocked session so a refresh does not re-prompt:
    // the PQC keys, the Stalwart mail credential, and the ONE-WAY login key (used
    // to silently re-mint a kmapi session token; it is not the password).
    saveSession(email, { pqc, mailCred, loginKey } = {}) {
      const b = read(email) || { createdAt: Date.now() };
      write(email, {
        ...b,
        identity: pqc !== undefined ? pqc : (b.identity || null),
        mailCred: mailCred !== undefined ? mailCred : (b.mailCred || null),
        loginKey: loginKey !== undefined ? loginKey : (b.loginKey || null),
      });
    },
    session(email) { const b = read(email); return b && b.mailCred ? { pqc: b.identity, mailCred: b.mailCred, loginKey: b.loginKey } : null; },
    clearSession(email) { const b = read(email); if (b) write(email, { ...b, mailCred: null, loginKey: null }); },
  }), []);
}
